import {API_BASE_URL} from '../../../security/ordinarySession';
import {apiFetch} from '../../../security/apiFetch';
import { AUDIO_SETTINGS, ERROR_MESSAGES } from '../constants';

/** Authenticated, bounded download shared by all player implementations. */
export const loadAudioSource=async(url:string,token?:string|null,signal?:AbortSignal):Promise<string>=>{
 const controller=new AbortController(),abort=()=>controller.abort();
 signal?.addEventListener('abort',abort);if(signal?.aborted)controller.abort();
 const timer=setTimeout(abort,AUDIO_SETTINGS.LOAD_TIMEOUT);
 try{
  const base=new URL(API_BASE_URL||'/',window.location.origin),target=new URL(url,base);
  if(!['http:','https:'].includes(target.protocol)||target.username||target.password||target.searchParams.has('token')||target.searchParams.has('access_token'))throw new Error(ERROR_MESSAGES.AUDIO_PLAYBACK);
  const headers:HeadersInit={};if(token&&target.origin===base.origin)headers.Authorization=`Bearer ${token}`;
  const response=await apiFetch(target.href,{headers,signal:controller.signal,cache:'no-store',redirect:'error'});
  const mime=response.headers?.get('Content-Type')?.split(';')[0].trim().toLowerCase();
  if(!response.ok||!response.body||!mime?.startsWith('audio/')){await response.body?.cancel();throw new Error(ERROR_MESSAGES.AUDIO_PLAYBACK);}
  const limit=16*1024*1024,declared=Number(response.headers.get('Content-Length'));
  if(declared>limit){await response.body.cancel();throw new Error('Аудио превышает допустимый размер');}
  const reader=response.body.getReader(),chunks:Uint8Array[]=[];let size=0;
  try{while(true){const {done,value}=await reader.read();if(done)break;size+=value.byteLength;if(size>limit)throw new Error('Аудио превышает допустимый размер');chunks.push(value);}}
  finally{await reader.cancel();reader.releaseLock();}
  if(controller.signal.aborted)throw new DOMException('Aborted','AbortError');
  return URL.createObjectURL(new Blob(chunks,{type:mime}));
 }finally{clearTimeout(timer);controller.abort();signal?.removeEventListener('abort',abort);}
};
let currentPlayback:(()=>void)|null=null;
export const stopCurrentAudio=()=>{currentPlayback?.();currentPlayback=null;};
export const playAudio=async(url:string,token?:string|null,options:{volume?:number;loop?:boolean}={}):Promise<void>=>{
 stopCurrentAudio();const controller=new AbortController();let source:string|null=null,audio:HTMLAudioElement|null=null;
 const release=()=>{controller.abort();if(audio){audio.onended=null;audio.onerror=null;audio.pause();audio.src='';}if(source){URL.revokeObjectURL(source);source=null;}if(currentPlayback===release)currentPlayback=null;};
 currentPlayback=release;
 try{source=await loadAudioSource(url,token,controller.signal);if(controller.signal.aborted){release();return;}
  audio=new Audio(source);audio.volume=options.volume??AUDIO_SETTINGS.VOLUME;audio.loop=options.loop??false;audio.onended=release;audio.onerror=release;await audio.play();
 }catch(error){release();throw error;}
};

/**
 * Предзагрузка аудио файла
 */
export const preloadAudio = (url: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();

    audio.oncanplaythrough = () => {
      console.log('✅ Audio preloaded:', url);
      resolve();
    };

    audio.onerror = (e) => {
      console.error('❌ Audio preload error:', e);
      reject(new Error(`Failed to preload audio: ${url}`));
    };

    audio.src = url;
    audio.load();
  });
};

/**
 * Предзагрузка нескольких аудио файлов
 */
export const preloadMultipleAudio = async (urls: string[]): Promise<void> => {
  try {
    await Promise.all(urls.map(url => preloadAudio(url)));
    console.log('✅ All audio files preloaded');
  } catch (error) {
    console.error('❌ Some audio files failed to preload:', error);
    throw error;
  }
};

/**
 * Проверка поддержки аудио форматов
 */
export const checkAudioSupport = (): { webm: boolean; mp3: boolean; ogg: boolean } => {
  const audio = new Audio();

  return {
    webm: audio.canPlayType('audio/webm') !== '',
    mp3: audio.canPlayType('audio/mpeg') !== '',
    ogg: audio.canPlayType('audio/ogg') !== ''
  };
};

/**
 * Получение оптимального формата аудио для браузера
 */
export const getOptimalAudioFormat = (): string => {
  const support = checkAudioSupport();

  if (support.webm) return 'webm';
  if (support.mp3) return 'mp3';
  if (support.ogg) return 'ogg';

  return 'mp3'; // Fallback
};

/**
 * Создание AudioContext для анализа аудио
 */
export const createAudioContext = (): AudioContext | null => {
  try {
    // @ts-ignore
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    return new AudioContext();
  } catch (error) {
    console.error('❌ AudioContext not supported:', error);
    return null;
  }
};

/**
 * Анализ аудио через Web Audio API
 */
export const analyzeAudio = async (audioBlob: Blob): Promise<{
  duration: number;
  volume: number;
  frequency: number;
}> => {
  return new Promise((resolve, reject) => {
    const audioContext = createAudioContext();
    if (!audioContext) {
      reject(new Error('AudioContext not supported'));
      return;
    }

    const fileReader = new FileReader();
    fileReader.onload = async () => {
      try {
        const arrayBuffer = fileReader.result as ArrayBuffer;
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const duration = audioBuffer.duration;

        // Анализ громкости
        const channelData = audioBuffer.getChannelData(0);
        let sum = 0;
        for (let i = 0; i < channelData.length; i++) {
          sum += Math.abs(channelData[i]);
        }
        const volume = sum / channelData.length;

        // Простой анализ частоты (средняя частота)
        const frequency = audioBuffer.sampleRate / 2; // Упрощенный расчет

        resolve({
          duration,
          volume,
          frequency
        });
      } catch (error) {
        reject(error);
      }
    };

    fileReader.onerror = () => {
      reject(new Error('Failed to read audio file'));
    };

    fileReader.readAsArrayBuffer(audioBlob);
  });
};
